// pages/ProductCompatibilityResultPage.tsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  postProductCompatibility,
  getProductView,
  type ProductCompatResponse,
  type ProductCompatPayload,
  type ProductCompatRuleHit,
} from '../api/endpoints';
import { API_BASE_URL } from '../api/config';
import useFavoriteProduct from '../hooks/useFavoriteProduct';
import type { ProductView } from '../types/api';
import { AnimatePresence, motion } from 'framer-motion';
import { postCompatCompare } from '../api/telemetry';
import AIDisclaimerFooter from '../components/AIDisclaimerFooter';
import { getErrorMessage } from '../utils/errors';
import { shareOrCopyUrl } from '../utils/scannerIngredient';
import { createProductReference, type CompatibilityUsage } from '../utils/productIdentity';

/* ================= Icons ================= */
const cx = 'w-[18px] h-[18px]';
const IconGauge = () => (
  <svg viewBox="0 0 24 24" className={cx} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 21a9 9 0 1 1 9-9" />
    <path d="M12 12l4-4" />
  </svg>
);
const IconAlert = () => (
  <svg viewBox="0 0 24 24" className={cx} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3l-8.47-14.14a2 2 0 0 0-3.42 0Z" />
    <path d="M12 9v4" />
    <path d="M12 17h.01" />
  </svg>
);
const IconList = () => (
  <svg viewBox="0 0 24 24" className={cx} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 6h13" />
    <path d="M8 12h13" />
    <path d="M8 18h13" />
    <path d="M3 6h.01" />
    <path d="M3 12h.01" />
    <path d="M3 18h.01" />
  </svg>
);
const IconCopy = () => (
  <svg viewBox="0 0 24 24" className={cx} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);
const IconRefresh = () => (
  <svg viewBox="0 0 24 24" className={cx} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10" />
    <polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.13-3.36L23 10" />
    <path d="M20.49 15A9 9 0 0 1 6.36 18.36L1 14" />
  </svg>
);
const IconShare = () => (
  <svg viewBox="0 0 24 24" className={cx} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 12v7a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-7" />
    <path d="M16 6l-4-4-4 4" /><path d="M12 2v14" />
  </svg>
);

/* ================ Utils ================ */
function normalizeImage(url?: string | null): string | null {
  if (!url) return null;
  if (/^https?:/i.test(url)) return url;
  const base = API_BASE_URL.replace(/\/+$/, '');
  const rawPath = url.startsWith('/') ? url : `/${url}`;
  const dedupPath = base.endsWith('/v1') && rawPath.startsWith('/v1/') ? rawPath.slice(3) : rawPath;
  return `${base}${dedupPath}`;
}
function extractProductId(url?: string | null): number | null {
  if (!url) return null;
  const m = /\/product\/(\d+)\//.exec(url);
  if (!m) return null;
  const id = Number(m[1]);
  return Number.isFinite(id) ? id : null;
}
type NavState = {
  response?: ProductCompatResponse;
  payload?: ProductCompatPayload;
  productIds?: { a?: number; b?: number };
};
type ProductInfo = {
  productId: number | null;
  barcode: string;
  imageUrl: string | null;
  details?: ProductView | null;
};
function clamp(n: number, a: number, b: number) { return Math.max(a, Math.min(b, n)); }
function toneFromScore(score: number | null): 'good' | 'warn' | 'bad' {
  if (score === null) return 'warn';
  if (score >= 70) return 'good';
  if (score >= 40) return 'warn';
  return 'bad';
}
function usageFromContext(context?: string): { a: CompatibilityUsage; b: CompatibilityUsage } {
  if (!context || context === 'auto') return { a: 'auto', b: 'auto' };
  const [first, second] = context.split('_');
  const decode = (value?: string): CompatibilityUsage =>
    value === 'leaveon' ? 'leave_on' : value === 'leaveoff' ? 'leave_off' : 'auto';
  return { a: decode(first), b: decode(second) };
}
const nice = (s: string) => s.replace(/_/g, ' ');

async function copyCompatibilityJson(response: ProductCompatResponse | null): Promise<void> {
  if (!response) return;
  try {
    await navigator.clipboard.writeText(JSON.stringify(response, null, 2));
  } catch {
    /* clipboard unavailable */
  }
}

async function shareCompatibilityResult(): Promise<void> {
  try {
    await shareOrCopyUrl('Compatibility result', window.location.href);
  } catch {
    /* share/copy cancelled or unavailable */
  }
}

type EvidenceSide = {
  id?: number;
  name?: string;
  percent?: number;
  why?: Record<string, string | number | boolean>;
};

type EvidencePair = {
  left?: EvidenceSide;
  right?: EvidenceSide;
  score: number;
};

/* ========================================================================== */
export default function ProductCompatibilityResultPage(): React.JSX.Element {
  const nav = useNavigate();
  const location = useLocation();
  const state = (location.state ?? {}) as NavState;
  const [params] = useSearchParams();

  console.log('[PAGE LOAD] ProductCompatibilityResultPage - Initial state:', {
    has_state_response: !!state.response,
    has_state_payload: !!state.payload,
    state_productIds: state.productIds,
    url_params: { a: params.get('a'), b: params.get('b') }
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [response, setResponse] = useState<ProductCompatResponse | null>(state.response ?? null);
  const [payload, setPayload] = useState<ProductCompatPayload | null>(state.payload ?? null);
  const [productIds, setProductIds] = useState<{ a?: number; b?: number } | undefined>(state.productIds);
  const [productA, setProductA] = useState<ProductView | null>(null);
  const [productB, setProductB] = useState<ProductView | null>(null);
  const [showJSON, setShowJSON] = useState(false);
  const [telemetrySent, setTelemetrySent] = useState(false);

  useEffect(() => {
    if (response) return;
    const a = params.get('a');
    const b = params.get('b');
    const pa = params.get('pa'); const pb = params.get('pb');
    if ((!a && !pa) || (!b && !pb)) return;
    const ctx = params.get('context') ?? undefined;
    const ord = params.get('order') ?? undefined;
    const payloadFromUrl: ProductCompatPayload = {
      barcode_a: a ?? '',
      barcode_b: b ?? '',
      ...(pa ? { product_id_a: Number(pa) } : {}),
      ...(pb ? { product_id_b: Number(pb) } : {}),
      ...(ctx ? { context: ctx } : {}),
      ...(ord ? { order: ord } : {}),
    };
    setPayload(payloadFromUrl);
    setLoading(true);
    postProductCompatibility(payloadFromUrl)
      .then((res) => {
        setResponse(res);
      })
      .catch((error_: unknown) => { setError(getErrorMessage(error_, 'Failed to load comparison')); })
      .finally(() => { setLoading(false); });
  }, [response, params]);

  useEffect(() => {
    if (response && !response.ok) setError(response.message ?? 'Compatibility could not be evaluated.');
  }, [response]);

  useEffect(() => {
    if (!response?.ok) return;
    const idA = extractProductId(response.image_url_a ?? undefined) ?? state.productIds?.a;
    const idB = extractProductId(response.image_url_b ?? undefined) ?? state.productIds?.b;

    console.log('[TELEMETRY DEBUG] Compat Compare - Extracting IDs:', {
      image_url_a: response.image_url_a,
      image_url_b: response.image_url_b,
      extracted_id_a: idA,
      extracted_id_b: idB,
      productIds_state: state.productIds,
    });

    setProductIds({ a: idA ?? undefined, b: idB ?? undefined });

    if (idA) void getProductView(idA).then(setProductA).catch(() => { setProductA(null); });
    if (idB) void getProductView(idB).then(setProductB).catch(() => { setProductB(null); });
  }, [response, state.productIds]);

  useEffect(() => {
    console.log('[TELEMETRY USEEFFECT] Compat Compare useEffect triggered:', {
      has_response: !!response,
      response_ok: response?.ok,
      telemetrySent,
      productIds,
      will_run: !!response && response.ok && !telemetrySent
    });

    if (!response?.ok || telemetrySent) return;

    const idA = productIds?.a ?? extractProductId(response.image_url_a ?? undefined);
    const idB = productIds?.b ?? extractProductId(response.image_url_b ?? undefined);

    console.log('[TELEMETRY DEBUG] Extracted IDs for compat compare:', { idA, idB });

    if (idA && idB) {
      console.log('[TELEMETRY DEBUG] Sending compat compare telemetry:', idA, idB);
      void postCompatCompare(idA, idB).catch((error_: unknown) => {
        console.error('[TELEMETRY ERROR] Compat compare failed:', error_);
      });
      setTelemetrySent(true);
    } else if (!idA || !idB) {
      console.warn('[TELEMETRY DEBUG] Missing product IDs for compat compare:', { idA, idB });
    }
  }, [response, productIds, telemetrySent]);

  const comparison = response?.comparison;
  const normalizedScore = comparison?.normalized_score ?? null;
  const totalScore = comparison?.total_score ?? null;
  const tone = toneFromScore(normalizedScore);

  const infoA: ProductInfo = useMemo(
    () => ({
      productId: productIds?.a ?? extractProductId(response?.image_url_a ?? null),
      barcode: response?.barcode_a ?? payload?.barcode_a ?? '—',
      imageUrl: normalizeImage(response?.image_url_a),
      details: productA,
    }),
    [productIds?.a, response?.image_url_a, response?.barcode_a, payload?.barcode_a, productA],
  );

  const infoB: ProductInfo = useMemo(
    () => ({
      productId: productIds?.b ?? extractProductId(response?.image_url_b ?? null),
      barcode: response?.barcode_b ?? payload?.barcode_b ?? '—',
      imageUrl: normalizeImage(response?.image_url_b),
      details: productB,
    }),
    [productIds?.b, response?.image_url_b, response?.barcode_b, payload?.barcode_b, productB],
  );

  const favoriteA = useFavoriteProduct({ productId: infoA.productId ?? undefined, disabled: !infoA.productId });
  const favoriteB = useFavoriteProduct({ productId: infoB.productId ?? undefined, disabled: !infoB.productId });

  const handleRetry = useCallback(() => {
    if (!payload) { nav('/compatibility'); return; }
    setLoading(true);
    setError(null);
    postProductCompatibility(payload)
      .then((res) => { setResponse(res); })
      .catch((error_: unknown) => { setError(getErrorMessage(error_, 'Failed to refresh comparison')); })
      .finally(() => { setLoading(false); });
  }, [nav, payload]);

  const copyJSON = useCallback(() => {
    void copyCompatibilityJson(response);
  }, [response]);

  return (
    <>
      <div className="stack-4" style={{ minWidth: 0 }}>

        {response?.charged === false && (
          <div className="pill pill--teal" style={{ alignSelf: 'flex-start' }}>
            <span className="dot" />
            Already checked — no tokens used
          </div>
        )}

        <motion.div
          layout
          className="card card--bordered"
          style={{ position: 'relative', overflow: 'hidden', padding: 0 }}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.32, ease: [0.22, 0.61, 0.36, 1] }}
        >
          <div
            className="scan-pattern"
            style={{ position: 'absolute', inset: 0, opacity: 0.3, pointerEvents: 'none' }}
          />

          <div className="stack-4" style={{ position: 'relative', padding: 'var(--s-5)' }}>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--s-3)' }}>
              <div
                className="icon-ring"
                style={{ width: 'var(--s-7)', height: 'var(--s-7)', flexShrink: 0 }}
              >
                <IconGauge />
              </div>
              <div className="row-3" style={{ flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => { setShowJSON((v) => !v); }}
                  className="btn btn--ghost btn--sm hidden-mobile"
                  style={{ display: 'none' }}
                >
                  <IconList /> {showJSON ? 'Hide raw' : 'Show raw'}
                </button>
                <button
                  type="button"
                  onClick={copyJSON}
                  className="btn btn--ghost btn--sm"
                  style={{ display: 'none' }}
                >
                  <IconCopy /> Copy JSON
                </button>
                <button type="button" onClick={() => { void shareCompatibilityResult(); }} className="btn btn--ghost btn--sm">
                  <IconShare /> Share
                </button>
              </div>
            </div>

            <div style={{ minWidth: 0 }}>
              <h1 className="t-h2" style={{ margin: 0 }}>
                Compatibility <em className="t-italic t-teal">result.</em>
              </h1>
              <p className="t-small t-mute" style={{ margin: 'var(--s-2) 0 0' }}>
                How these two products work together — rules, preferences, and evidence.
              </p>
            </div>

            <motion.div layout className="stack-3">
              <DialCard score={normalizedScore} total={totalScore} tone={tone} response={response} />

              {response?.ok !== false && (
                <div className="g-2">
                  <ProductCard side="a" info={infoA} favorite={favoriteA} />
                  <ProductCard side="b" info={infoB} favorite={favoriteB} />
                </div>
              )}
            </motion.div>

            <div className="row-3" style={{ flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() =>
                  { nav('/compatibility', {
                    state: {
                      products: [
                        createProductReference({
                          product_id: infoA.productId ?? undefined,
                          barcode: infoA.barcode,
                          brand: infoA.details?.brand,
                          name: infoA.details?.name,
                          image_url: infoA.details?.image_url ?? infoA.imageUrl ?? undefined,
                        }),
                        createProductReference({
                          product_id: infoB.productId ?? undefined,
                          barcode: infoB.barcode,
                          brand: infoB.details?.brand,
                          name: infoB.details?.name,
                          image_url: infoB.details?.image_url ?? infoB.imageUrl ?? undefined,
                        }),
                      ],
                      prefill: {
                        barcodeA: payload?.barcode_a,
                        barcodeB: payload?.barcode_b,
                        productIdA: infoA.productId ?? undefined,
                        productIdB: infoB.productId ?? undefined,
                      },
                      usage: usageFromContext(payload?.context ?? response?.context),
                    },
                  }); }
                }
                className="btn btn--ghost btn--sm"
              >
                Run another comparison
              </button>
              <button
                type="button"
                onClick={handleRetry}
                className="btn btn--ghost btn--sm"
                disabled={loading}
              >
                <IconRefresh /> Refresh result
              </button>
            </div>

            <AnimatePresence initial={false}>
              {showJSON && (
                <motion.pre
                  key="raw"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="raw-code"
                  style={{ marginTop: 'var(--s-3)' }}
                >
                  {JSON.stringify(response, null, 2)}
                </motion.pre>
              )}
            </AnimatePresence>
          </div>
        </motion.div>

        {loading && (
          <div className="card card--bordered" style={{ padding: 'var(--s-5)' }}>
            <p className="t-small t-mute" style={{ margin: 0 }}>Loading…</p>
          </div>
        )}

        {error && (
          <div className="card card--alert" style={{ padding: 'var(--s-5)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--s-3)' }}>
              <p className="t-small" style={{ margin: 0, color: 'var(--c-coral)', wordBreak: 'break-word', minWidth: 0 }}>{error}</p>
              <button
                onClick={handleRetry}
                className="btn btn--ghost btn--sm"
                style={{ flexShrink: 0, color: 'var(--c-coral)', borderColor: 'var(--c-coral)' }}
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {response && (
          <Section title="Overview" icon={<IconList />}>
            <div className="g-2">
              <MetaLine k="Barcode A" v={response.barcode_a} />
              <MetaLine k="Barcode B" v={response.barcode_b} />
              <MetaLine k="Context" v={response.context} />
              {comparison && <MetaLine k="Abstain" v={comparison.abstain ? 'Yes' : 'No'} />}
            </div>
          </Section>
        )}

        {comparison?.warnings && comparison.warnings.length > 0 && (
          <Section title="Warnings" icon={<IconAlert />}>
            <ul className="stack-2" style={{ margin: 0, padding: 0, listStyle: 'none' }}>
              {comparison.warnings.map((w) => (
                <li key={w} style={{ display: 'flex', gap: 'var(--s-2)', alignItems: 'flex-start' }}>
                  <span style={{ color: 'var(--c-coral)', flexShrink: 0 }}>·</span>
                  <span className="t-small" style={{ color: 'var(--c-coral)', wordBreak: 'break-word' }}>{w}</span>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {comparison?.rule_hits && comparison.rule_hits.length > 0 && (
          <Section title="Rule hits" icon={<IconList />}>
            <div className="stack-3">
              {comparison.rule_hits.map((hit, i) => (
                <RuleHit key={`${hit.rule_id}-${i}`} hit={hit} />
              ))}
            </div>
          </Section>
        )}

        {comparison?.preference_adjustments && comparison.preference_adjustments.length > 0 && (
          <Section title="Preference adjustments" icon={<IconList />}>
            <div className="stack-3">
              {comparison.preference_adjustments.map((adj) => (
                <motion.div
                  key={adj.code}
                  initial={{ opacity: 0, y: 6 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-50px' }}
                  className="card card--bordered card--flat"
                  style={{
                    padding: 'var(--s-3)',
                    display: 'flex', justifyContent: 'space-between',
                    alignItems: 'flex-start', gap: 'var(--s-3)',
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div className="t-small" style={{ fontWeight: 500, color: 'var(--ink)', wordBreak: 'break-word' }}>
                      {adj.description}
                    </div>
                    <div className="t-small t-mute">
                      {adj.code} · weight {typeof adj.weight === 'number' ? adj.weight.toFixed(2) : adj.weight}
                    </div>
                  </div>
                  <div
                    className="t-small"
                    style={{
                      fontWeight: 600, flexShrink: 0,
                      color: adj.delta >= 0 ? 'var(--c-kivo-teal)' : 'var(--c-coral)',
                    }}
                  >
                    {adj.delta >= 0 ? '+' : ''}{adj.delta.toFixed(2)}
                  </div>
                </motion.div>
              ))}
            </div>
          </Section>
        )}

        {comparison?.explanations && comparison.explanations.length > 0 && (
          <Section title="Explanations" icon={<IconList />}>
            <ul className="stack-2" style={{ margin: 0, padding: 0, listStyle: 'none' }}>
              {comparison.explanations.map((ex, idx) => (
                <li key={idx} style={{ display: 'flex', gap: 'var(--s-2)', alignItems: 'flex-start' }}>
                  <span className="t-mute" style={{ flexShrink: 0 }}>·</span>
                  <span className="t-small" style={{ wordBreak: 'break-word' }}>{ex}</span>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {comparison?.unmatched_ingredients && Object.keys(comparison.unmatched_ingredients).length > 0 && (
          <Section title="Unmatched ingredients" icon={<IconList />}>
            <div className="stack-3">
              {Object.entries(comparison.unmatched_ingredients).map(([pid, list]) => (
                <div key={pid} className="card card--sand card--flat" style={{ padding: 'var(--s-3)' }}>
                  <div className="t-label" style={{ color: 'var(--ink)', marginBottom: 'var(--s-1)' }}>
                    Product {pid}
                  </div>
                  <p className="t-small" style={{ margin: 0, wordBreak: 'break-word' }}>
                    {list.join(', ')}
                  </p>
                </div>
              ))}
            </div>
          </Section>
        )}
      </div>

      <AIDisclaimerFooter />
    </>
  );
}

/* =================== Sub-components =================== */

function DialCard({
  score,
  total,
  tone,
  response,
}: {
  score: number | null;
  total: number | null;
  tone: 'good' | 'warn' | 'bad';
  response: ProductCompatResponse | null;
}) {
  const pct = score == null ? 50 : clamp(score, 0, 100);
  const ringFill  = tone === 'good' ? 'var(--c-kivo-teal)' : tone === 'warn' ? 'var(--c-deep-teal)' : 'var(--c-coral)';
  const ringTrack = tone === 'bad'  ? 'rgba(244,168,142,.18)' : 'var(--c-teal-weak)';
  const chipLabel = score == null ? 'Pending' : score >= 70 ? 'Compatible' : score >= 40 ? 'Moderate' : 'Review';
  const chipMod   = tone === 'good' ? 'pill--teal' : 'pill--coral';

  return (
    <motion.div layout className="card card--bordered row-3" style={{ padding: 'var(--s-3)', minWidth: 0 }}>
      <div style={{ flexShrink: 0 }}>
        <div
          className="ring"
          style={{
            '--pct':        pct,
            '--size':       'var(--s-10)',
            '--ring-fill':  ringFill,
            '--ring-track': ringTrack,
          } as React.CSSProperties}
        >
          <div className="ring__inner">
            <div className="ring__num">{score == null ? '—' : Math.round(score)}</div>
            <div className="ring__cap">Score</div>
          </div>
        </div>
      </div>

      <div style={{ minWidth: 0 }}>
        <div className="row-3" style={{ flexWrap: 'wrap' }}>
          <span className={`pill ${chipMod}`}>{chipLabel}</span>
          {response?.comparison?.abstain && <span className="pill">Abstain</span>}
        </div>
        <div className="stack-2" style={{ marginTop: 'var(--s-2)' }}>
          {total != null && (
            <div className="t-small">
              <span className="t-mute">Raw score:</span>{' '}
              <span style={{ fontWeight: 500 }}>{total.toFixed(2)}</span>
            </div>
          )}
          {response?.context && (
            <div className="t-small">
              <span className="t-mute">Context:</span>{' '}
              <span style={{ fontWeight: 500, wordBreak: 'break-word' }}>{response.context}</span>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}

function Section({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  const [open, setOpen] = useState(true);
  return (
    <section className="card card--bordered" style={{ padding: 0 }}>
      <button
        type="button"
        onClick={() => { setOpen((v) => !v); }}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: 'var(--s-3) var(--s-4)', background: 'transparent', border: 'none', cursor: 'pointer',
        }}
      >
        <div className="row-3">
          <div
            className="icon-ring"
            style={{ width: 'var(--s-6)', height: 'var(--s-6)', flexShrink: 0 }}
          >
            {icon}
          </div>
          <h2 className="t-small" style={{ margin: 0, fontWeight: 600, color: 'var(--ink)' }}>{title}</h2>
        </div>
        <ChevronIcon open={open} />
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            style={{ overflow: 'hidden', borderTop: '1px solid var(--rule)' }}
          >
            <div style={{ padding: 'var(--s-3) var(--s-4) var(--s-5)' }}>
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

function MetaLine({ k, v }: { k: string; v: string }) {
  return (
    <div
      className="card card--sand card--flat"
      style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--s-3)', padding: 'var(--s-2) var(--s-3)' }}
    >
      <span className="t-small t-mute">{k}</span>
      <span className="t-small" style={{ fontWeight: 500, wordBreak: 'break-all' }}>{v}</span>
    </div>
  );
}

function WhyChips({ why, side }: { why: Record<string, string | number | boolean>; side: 'left' | 'right' }) {
  const entries = Object.entries(why);
  if (entries.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--s-1)', marginTop: 'var(--s-1)' }}>
      {entries.map(([k, v]) => (
        <span
          key={k}
          className="pill pill--compact"
          title={`${side}: ${k}=${String(v)}`}
        >
          {nice(k)}{v === true ? '' : `=${String(v)}`}
        </span>
      ))}
    </div>
  );
}

function ProductCard({
  side,
  info,
  favorite,
}: {
  side: 'a' | 'b';
  info: ProductInfo;
  favorite: { favorited: boolean | null; loading: boolean; toggle: () => void };
}) {
  const label = `Product ${side.toUpperCase()} (${side === 'a' ? 'applied first' : 'applied second'})`;
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      className={`card card--bordered stack-3 compare-result-side compare-result-side--${side}`}
      data-testid={`compat-result-product-${side}`}
      style={{ padding: 'var(--s-3)', minWidth: 0 }}
    >
      <span className="t-label compare-result-side__label">
        <span className="compare-result-side__marker" aria-hidden />{label}
      </span>

      <div className="row-3" style={{ alignItems: 'flex-start', minWidth: 0 }}>
        {info.imageUrl ? (
          <div
            className="frame"
            style={{ width: 'var(--s-9)', height: 'var(--s-9)', display: 'grid', placeItems: 'center', flexShrink: 0 }}
          >
            <img
              src={info.imageUrl}
              alt={info.details?.name ?? label}
              style={{ maxHeight: 70, maxWidth: 60, objectFit: 'contain' }}
            />
          </div>
        ) : (
          <div
            style={{
              width: 'var(--s-9)', height: 'var(--s-9)', flexShrink: 0,
              borderRadius: 'var(--r-md)',
              border: '1.5px dashed var(--c-charcoal-30)',
              display: 'grid', placeItems: 'center',
            }}
          >
            <span className="t-small t-mute">No image</span>
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="t-small" style={{ fontWeight: 600, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {info.details?.name ?? 'Unknown product'}
          </div>
          <div className="t-small t-mute" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {info.details?.brand ?? 'Brand unavailable'}
          </div>
          <div className="t-small t-mute" style={{ marginTop: 'var(--s-1)', wordBreak: 'break-all' }}>
            Barcode {info.barcode}
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={favorite.toggle}
        disabled={favorite.loading || !info.productId}
        className="btn btn--ghost btn--sm btn--full"
        style={favorite.favorited ? { color: 'var(--c-coral)', borderColor: 'var(--c-coral)' } : undefined}
      >
        <svg
          style={{ width: 16, height: 16 }}
          viewBox="0 0 24 24"
          fill={favorite.favorited ? 'currentColor' : 'none'}
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M12 21s-6.4-4.35-9.2-8.06C1.02 11.66 1 8.65 3.2 6.4 5.4 4.2 8.7 4.1 10.9 6.3L12 7.41l1.1-1.1c2.2-2.2 5.5-2.1 7.7.1 2.2 2.24 2.18 5.26.2 6.54C18.4 16.65 12 21 12 21Z" />
        </svg>
        {favorite.favorited ? 'Remove from favorites' : 'Add to favorites'}
      </button>

      {!info.productId && (
        <p className="t-small t-mute" style={{ margin: 0 }}>Product ID unavailable — cannot favorite.</p>
      )}
    </motion.div>
  );
}

function EvidencePairRow({ pair, idx }: { pair: EvidencePair; idx: number }) {
  const left = pair.left;
  const right = pair.right;

  return (
    <div
      key={idx}
      className="card card--bordered card--flat card--compact"
      style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--s-2)' }}
    >
      <div className="compat-evidence-side compat-evidence-side--a" data-testid="compat-evidence-a">
        <div className="t-label compare-result-side__label" style={{ marginBottom: 'var(--s-1)' }}>
          <span className="compare-result-side__marker" aria-hidden />A
        </div>
        <div className="t-small" style={{ fontWeight: 500, wordBreak: 'break-word' }}>
          {left?.id ? (
            <Link
              to={`/ingredients/${left.id}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'var(--c-deep-teal)', transition: `color var(--t-fast) var(--ease)` }}
            >
              {left.name ?? '—'}
            </Link>
          ) : (left?.name ?? '—')}
          {left?.percent ? (
            <span className="t-mute"> · {left.percent}%</span>
          ) : null}
        </div>
        {left?.why && <WhyChips why={left.why} side="left" />}
      </div>

      <span className="t-small t-mute" style={{ flexShrink: 0, paddingTop: 'var(--s-1)' }}>×</span>

      <div className="compat-evidence-side compat-evidence-side--b" data-testid="compat-evidence-b">
        <div className="t-label compare-result-side__label" style={{ marginBottom: 'var(--s-1)' }}>
          <span className="compare-result-side__marker" aria-hidden />B
        </div>
        <div className="t-small" style={{ fontWeight: 500, wordBreak: 'break-word' }}>
          {right?.id ? (
            <Link
              to={`/ingredients/${right.id}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'var(--c-deep-teal)', transition: `color var(--t-fast) var(--ease)` }}
            >
              {right.name ?? '—'}
            </Link>
          ) : (right?.name ?? '—')}
          {right?.percent ? (
            <span className="t-mute"> · {right.percent}%</span>
          ) : null}
        </div>
        {right?.why && <WhyChips why={right.why} side="right" />}
      </div>

      <span className="t-small t-mute" style={{ flexShrink: 0, paddingTop: 'var(--s-1)' }}>
        {Math.round(pair.score)}%
      </span>
    </div>
  );
}

function RuleHit({ hit }: { hit: ProductCompatRuleHit }) {
  const [open, setOpen] = useState(false);

  const cardMod  = hit.kind === 'synergy' ? 'card--synergy' : hit.kind === 'conflict' ? 'card--conflict' : '';
  const chipMod  = hit.kind === 'synergy' ? 'pill--teal'    : hit.kind === 'conflict' ? 'pill--coral'    : '';

  const evidencePairs: EvidencePair[] = (hit.evidence_pairs ?? []).map((pair) => ({
    left: pair.left,
    right: pair.right,
    score: pair.score,
  }));

  return (
    <div className={`card card--bordered card--flat ${cardMod}`} style={{ padding: 'var(--s-3)', minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--s-2)' }}>
        <div style={{ minWidth: 0 }}>
          <div className="row-3" style={{ flexWrap: 'wrap', gap: 'var(--s-1)' }}>
            <span className={`pill pill--compact ${chipMod}`}>{hit.kind}</span>
            <span className="pill pill--compact">Rule {hit.rule_id}</span>
          </div>
          <div className="t-small" style={{ fontWeight: 600, color: 'var(--ink)', marginTop: 'var(--s-1)', wordBreak: 'break-word' }}>
            {hit.summary || hit.name}
          </div>
          <div className="t-small t-mute" style={{ marginTop: 'var(--s-1)' }}>
            Evidence: {Math.round(hit.avg_evidence)}% · Δ {typeof hit.final_delta === 'number' ? hit.final_delta.toFixed(1) : '—'}
          </div>
        </div>

        <button
          type="button"
          onClick={() => { setOpen((o) => !o); }}
          className="btn btn--ghost btn--sm"
          style={{ flexShrink: 0 }}
          aria-expanded={open}
        >
          {open ? 'Hide' : 'Show'}
        </button>
      </div>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="body"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            className="stack-3"
            style={{ marginTop: 'var(--s-3)' }}
          >
            {hit.note && (
              <p className="t-small t-mute" style={{ margin: 0, wordBreak: 'break-word' }}>{hit.note}</p>
            )}

            {hit.details && Object.keys(hit.details).length > 0 && (
              <div className="g-2">
                {Object.entries(hit.details).map(([k, v]) => (
                  <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--s-3)' }}>
                    <span className="t-small t-mute" style={{ textTransform: 'capitalize' }}>{nice(k)}</span>
                    <span className="t-small" style={{ fontWeight: 500, wordBreak: 'break-all' }}>{String(v)}</span>
                  </div>
                ))}
              </div>
            )}

            {evidencePairs.length > 0 && (
              <div className="card card--sand card--flat" style={{ padding: 'var(--s-3)' }}>
                <div className="t-label" style={{ color: 'var(--ink)', marginBottom: 'var(--s-2)' }}>
                  Evidence pairs
                </div>
                <div className="stack-2">
                  {evidencePairs.map((pair, idx) => (
                    <EvidencePairRow key={idx} pair={pair} idx={idx} />
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      style={{
        width: 18, height: 18,
        color: 'var(--ink-muted)',
        flexShrink: 0,
        transition: `transform var(--t-fast) var(--ease)`,
        transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
      }}
    >
      <polyline
        points="6 9 12 15 18 9"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
